import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TelegramAuthProvider } from './contexts/TelegramAuthContext';

// Components
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import TelegramRoute from './components/TelegramRoute/TelegramRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import QuranAPI from './pages/QuranAPI/QuranAPI';
import AIReports from './pages/AIReports/AIReports';
import {readerRoutes} from './components/ReaderRoutes';
import Users from './pages/Users/Users';
import Monitoring from './pages/Monitoring/Monitoring';
import Logs from './pages/Logs/Logs';
import Tokens from './pages/Tokens/Tokens';
import ArabicLessons from './pages/ArabicLessons/ArabicLessons';
import ArabicTerms from './pages/ArabicTerms/ArabicTerms';

// Theme configuration
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

function AdminAI(){const {token}=useAuth();return <AIReports accessToken={token}/>;}
function AdminQuran(){const {token}=useAuth();return <QuranAPI accessToken={token}/>;}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <Router basename="/platform-admin">
          <TelegramAuthProvider>
            <AuthProvider>
              <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/quran" element={
                  <ProtectedRoute requiredPermission="admin:read">
                    <Layout>
                      <AdminQuran />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/ai-reports" element={<ProtectedRoute requiredPermission="admin:read"><Layout><AdminAI/></Layout></ProtectedRoute>}/>
                {readerRoutes}
                <Route path="/users" element={
                  <ProtectedRoute requiredPermission="users:manage">
                    <Layout>
                      <Users />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/monitoring" element={
                  <ProtectedRoute requiredPermission="monitoring:read">
                    <Layout>
                      <Monitoring />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/logs" element={
                  <ProtectedRoute requiredPermission="admin:read">
                    <Layout>
                      <Logs />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/tokens" element={
                  <ProtectedRoute requiredPermission="tokens:manage">
                    <Layout>
                      <Tokens />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/arabic-lessons" element={
                  <TelegramRoute>
                    <ArabicLessons />
                  </TelegramRoute>
                } />
                <Route path="/arabic-terms" element={
                  <TelegramRoute>
                    <ArabicTerms />
                  </TelegramRoute>
                } />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Box>
            </AuthProvider>
          </TelegramAuthProvider>
        </Router>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;